import { Navigate } from 'react-router-dom';
import { Dashboard } from './pages/Dashboard';
import { FirmWorkspaceNotReady } from './advisor/FirmWorkspaceNotReady';
import { useAdvisorFirm, useHoldForAdvisorQuery } from './advisor/useAdvisorFirm';
import { useWorkspace } from './workspace/WorkspaceProvider';
import { useWorkspaceKind, isFirmWorkspace } from './workspace/useWorkspaceKind';

/**
 * The index route (`/`), ordinarily `Dashboard`. In the advisor's own firm workspace
 * (docs/25, steward-app#700) hiding the six family pillars from `Layout`'s nav does
 * NOT stop `/` from still rendering Dashboard's net-worth hero over a workspace with
 * no accounts — so redirect there to the advisor portal instead. Kept in its own
 * file (rather than inline in `router.tsx`) so it's unit-testable without importing
 * `router.tsx` itself, which calls `createBrowserRouter` at module load.
 *
 * `replace: true` keeps this hop out of back/forward history, same as every other
 * redirect this router issues. Not a loop risk against `{ path: '*' }` in `router.tsx`
 * (which itself redirects to `/`): a direct hit on an unknown path bounces here once,
 * and from here an advisor in their firm workspace bounces ONCE more to `/advisor`,
 * which is a real route, not `*` — the walk terminates in two hops either way.
 *
 * **Cold-load flash, and the hint that closes most of it (steward-app#700 follow-up).**
 * `Layout` and this component render in the same pass, and `useAdvisorFirm()`'s query has
 * not resolved yet on a cold load — `advisorFor` reads `null` while loading, same as it
 * does for a non-advisor. Naively, an advisor's every session would mount `<Dashboard />`
 * (firing ITS OWN queries against the firm workspace) for one request's worth of time,
 * then unmount it on the redirect. Rejected outright: holding every session's paint until
 * this query settles (`if (isLoading) return null`) would cost every family a serialized
 * round trip before Dashboard ever paints, to fix a flash only an advisor session has.
 *
 * So the hold is conditional on {@link rememberedFirmWorkspaceId}, a `localStorage` hint
 * of the firm workspace id this browser last resolved — see that function's header before
 * touching this. It is PAINT-TIMING ONLY: it never decides `inFirmWorkspace` on its own,
 * only whether to hold `null` for the beat until the real query answers. A stale or forged
 * value here costs at most one extra render's hold; once `isLoading` is false, the branch
 * below reads only `advisorFor`, exactly as before this hint existed.
 *
 * That gap — the advisor's very FIRST cold load in the firm workspace, nothing remembered yet
 * — was accepted while the fall-through was `<Dashboard />`: the wrong page for one beat.
 * steward-app#1212 made the fall-through a page that says their portal is not ready, which is
 * a claim about their account rather than a misplaced widget, so the hold now takes the
 * mounted App Pack as a second reason to wait (the third argument to
 * `useHoldForAdvisorQuery` below). Same bound, same "never a decision, only a wait".
 */
export function HomeRoute() {
  const { advisorFor, firmChoice, isLoading } = useAdvisorFirm();
  const { activeWorkspaceId, activeMembership, switchInFlight } = useWorkspace();
  // `isFirmWorkspace` (`workspace/useWorkspaceKind.ts`) is the real signal (appcore docs/26
  // Phase 0), plus a narrow stand-in for the window before `activeMembership` is known — its
  // own header has the dated account, and steward-app#736 is the extraction that keeps this
  // in lockstep with `Settings.tsx`, `useClientFrame.ts` and `RequireFamilyWorkspace.tsx`.
  //
  // ⚠️ **`placedAtSomeFirm &&` gates the WHOLE expression here, and that is DELIBERATE —
  // `Layout.tsx` now does the opposite, on purpose** (steward-app#1271 there,
  // steward-app#1307 for the two Settings gates). Do not "align" this file with them: the
  // three of them answer "is this workspace a firm's", a fact about the WORKSPACE that is
  // true whether or not this caller's own `GET /advisor/firms` has resolved. This
  // expression decides a REDIRECT to `/advisor`, which is a claim about the caller's
  // ACCOUNT — and redirecting someone the façade has not placed anywhere lands them on a
  // page telling them the advisor area is not for them. Without this gate that is exactly
  // where an unresolved caller goes; with it, the route falls through to the
  // `workspaceKind === 'firm'` branch at the bottom, which says the workspace is not ready
  // rather than showing a family dashboard over it (steward-app#1212).
  //
  // This paragraph used to justify the gate by the prod rollout runbook's deploy window
  // instead, and point at `Layout.tsx` for the fuller version. Both are gone: #1271
  // refuted the window (the founder hit "pack mounted, `advisorFor` null" twice on
  // 2026-09-07/08, in neither case during a deploy), and `Layout.tsx` now argues the
  // opposite of what this file needs. `useWorkspaceKind`'s header is where the full
  // account of which question is which now lives.
  const workspaceKind = useWorkspaceKind();
  //
  // ⚠️ **`firmChoice` joins the gate since steward-app#983, and it is NOT a general
  // relaxation of the gate above.** When the façade cannot place the caller at all —
  // `advisorFor: null` with NOTHING beside it, no `firmChoice` either — `placedAtSomeFirm`
  // stays false exactly as it did before #983 widened this to an OR; #983 only ever ADDS a
  // true, it never turns an already-false case true. A `firmChoice` is the opposite
  // situation from an unplaced caller: the façade HAS placed them at firms and is asking
  // WHICH, so treating them as "not an advisor" would be the option-1 lockout #983 rejected.
  //
  // Redirecting to `/advisor` is the RIGHT answer for them, not merely a tolerable one:
  // `RequireAdvisorFirm` renders the chooser there, so the operator lands on the question
  // instead of on a family Dashboard over a workspace that has no family data in it.
  const placedAtSomeFirm = !!advisorFor || !!firmChoice;
  const inFirmWorkspace =
    placedAtSomeFirm && isFirmWorkspace(workspaceKind, activeMembership, activeWorkspaceId, advisorFor);
  // Called BEFORE the early return below, as every hook must be: the hold, and its TIME
  // BOUND, live in one shared hook (steward-app#794) — `Layout`'s `SidebarContent` holds the
  // family pillars on exactly the same condition, and the two copies of this expression had
  // already drifted apart. Read that hook's header before touching this: the bound is what
  // keeps a hanging request from showing app chrome with no page body for half a minute, and
  // once it elapses this route falls through to `<Dashboard />` deliberately, even though the
  // query is still in flight.
  //
  // The third argument is steward-app#1212's, and it exists because the branch at the bottom
  // makes the flash EXPENSIVE. The `localStorage` hint is absent on an advisor's first-ever
  // cold load in the firm workspace, in cleared or blocked storage, and in any fresh browser;
  // before #1212 those sessions flashed `<Dashboard />`, which was merely the wrong page,
  // and they would now flash "your advisor portal is not ready", which is a claim about the
  // person's account. The mounted pack says the workspace IS a firm's, which is reason enough
  // to wait one beat — under the SAME bound, so a hanging query still degrades to a
  // navigable page rather than an indefinitely blank one.
  const holdForAdvisorQuery = useHoldForAdvisorQuery(isLoading, activeWorkspaceId, workspaceKind === 'firm');
  // ⚠️ **`switchInFlight` holds ALL THREE branches below, not just the redirect, and
  // removing it reintroduces the steward-app#1409 race one hop earlier than the bug that
  // name covers.** `exitToClients` (`useClientFrame.ts`) calls `switchWorkspace(firmWorkspaceId,
  // ADVISOR_CLIENTS_PATH)` from the CLIENT workspace's own `/` — i.e. from THIS route.
  // `setSelectedId` inside `switchWorkspace` is an ordinary synchronous `setState`;
  // `navigate('/advisor/clients', ...)` is wrapped in `React.startTransition` by react-router
  // 7. React commits the synchronous update first, so that click produces at least one real,
  // painted render where `activeWorkspaceId` (and so `workspaceKind`) already read the FIRM
  // workspace while `useLocation()` still reads `/`, meaning THIS component is still mounted.
  // `inFirmWorkspace` and `workspaceKind === 'firm'` both go true on exactly that render.
  //
  // A first draft of this fix guarded only the `<Navigate>` line with `!switchInFlight` and
  // left the `workspaceKind === 'firm'` branch below bare — code review caught that this
  // does not fix the flash, it only moves it: skipping the redirect on that render falls
  // through to `holdForAdvisorQuery` (false — the advisor's own `GET /advisor/firms` already
  // resolved, or `switchWorkspace` could not have had a real `firmWorkspaceId` to switch to)
  // and then to the UNGUARDED `<FirmWorkspaceNotReady />` branch, painting "your advisor
  // portal is not ready" for the same one frame the redirect used to hijack instead. Holding
  // the WHOLE decision — render nothing while a switch is headed somewhere else, exactly
  // like the `holdForAdvisorQuery` line already does for an in-flight query — is what
  // actually removes the flash rather than relocating it.
  //
  // `switchInFlight` (`WorkspaceProvider`'s own docblock has the mechanism) is what tells
  // this transitional commit apart from a caller who has always been in the firm workspace
  // or always in an ordinary family one, where it is false and every branch below behaves
  // exactly as before this fix. Discovered via the 2026-09-12 nightly E2E run's
  // `advisor-portal.spec.ts` failure (steward-app#1412); `RequireAdvisorFirm.tsx`'s
  // `!switchInFlight` guard on its own self-heal effect is the precedent.
  if (switchInFlight) return null;
  if (inFirmWorkspace) return <Navigate to="/advisor" replace />;
  if (holdForAdvisorQuery) return null;
  // ⚠️ **The family dashboard is never what a FIRM workspace shows** (steward-app#1212).
  // The branch above needs `placedAtSomeFirm`, so a firm workspace whose advisor context the
  // server could not resolve — an environment that has mounted the pack but not caught up
  // with the firm's configuration, which is both the documented rollout window and what stage
  // ran on 2026-09-03 — fell through to `<Dashboard />`: a net-worth hero over a workspace
  // that holds no family's money, six queries fired at it, and on stage a blank page.
  //
  // `workspaceKind` alone is the right test here and does NOT need the `!!advisorFor` gate the
  // expression above carries. That gate exists because `inFirmWorkspace` REDIRECTS, and a
  // redirect into `/advisor` for someone the server has not placed lands them on "this area is
  // for advisory-firm team members". This branch redirects nowhere and claims nothing about
  // who they are; it says the workspace they are standing in is a firm's, which the mounted
  // pack is direct evidence of.
  if (workspaceKind === 'firm') return <FirmWorkspaceNotReady />;
  return <Dashboard />;
}
